import React, { useState, useEffect, forwardRef, useRef } from "react";
import {
  Box,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Input,
  Select,
  IconButton,
  Flex,
  useColorModeValue,
  useTheme, FormErrorMessage
} from "@chakra-ui/react";
import {DeleteIcon, AddIcon} from "@chakra-ui/icons";
import SearchSelect from "../SearchSelect/SearchSelect";
import ConfirmDialog from "../Dialog/ConfirmDialog";
import InputMask from "react-input-mask";
import { useTranslation } from "react-i18next";

const DynamicTable = forwardRef(({
                                   columns = [],
                                   value = [],
                                   onChange,
                                   id,
                                   name,
                                   isInvalid = false,
                                   isDisabled = false,
                                   isReadOnly = false,
                                   emptyMessage,
                                   errorMessage,
                                   addRowLabel,
                                   componentTypes = {},
                                   cellErrors = {},
                                   ...props
                                 }, ref) => {
  const theme = useTheme();

  const tableBg = useColorModeValue("white", "gray.800");
  const headerBg = useColorModeValue("#f6f9fc", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const inputBg = useColorModeValue("#fff", "gray.700");
  const inputColor = useColorModeValue("gray.700", "white");
  const inputBorderColor = useColorModeValue("gray.300", "gray.500");
  const inputHoverBorderColor = useColorModeValue("gray.400", "gray.400");
  const inputFocusBorderColor = useColorModeValue("blue.400", "blue.300");
  const buttonBg = useColorModeValue("blue.500", "blue.300");
  const buttonColor = useColorModeValue("white", "gray.800");
  const buttonHoverBg = useColorModeValue("blue.600", "blue.400");
  const deleteIconColor = useColorModeValue("red.500", "red.300");
  const deleteDialogRef = useRef(undefined);
  const [currentSelectedId, setCurrentSelectedId] = useState(undefined);
  const { t } = useTranslation();
  const rows = value || [];

  const fieldId = id || name;

  const handleAddRow = () => {
    if (isDisabled || isReadOnly) return;

    const newRow = {};
    columns.forEach(col => {
      newRow[col.key] = col.defaultValue !== undefined ? col.defaultValue : "";
    });

    onChange?.({
      target: {
        name: name || id,
        id: fieldId,
        value: [...rows, newRow]
      }
    });
  };

  const handleRemoveRow = (id) => {
    setCurrentSelectedId(id);
    deleteDialogRef.current?.displayDialog({
      message: t("system.actions.remove-confirm"),
    });
  };

  const removeRow = (index) => {
    if (isDisabled || isReadOnly) return;

    const updatedRows = [...rows];
    updatedRows.splice(index, 1);

    if (onChange) {
      const event = {
        target: {
          name: name || id,
          id: fieldId,
          value: updatedRows
        }
      };
      onChange(event);
    }
  };

  const handleConfirmRemove = async () => {
    if (currentSelectedId !== undefined) {
      removeRow(currentSelectedId);
      setCurrentSelectedId(undefined)
    }
  };

  const handleCellChange = (rowIndex, columnKey, cellValue) => {
    if (isDisabled || isReadOnly) return;

    const updatedRows = [...rows];
    updatedRows[rowIndex][columnKey] = cellValue;


    if (onChange) {
      const event = {
        target: {
          name: name || id,
          id: fieldId,
          value: updatedRows
        }
      };
      onChange(event);
    }
  };

  const createChangeHandler = (rowIndex, columnKey, column) => {
    return (value) => {

      if (value && value.target) {
        handleCellChange(rowIndex, columnKey, value.target.value);
      } else if (value && value.name !== undefined && value.value !== undefined) {
        handleCellChange(rowIndex, columnKey, value.value);
      } else {
        handleCellChange(rowIndex, columnKey, value);
      }
    };
  };

  const argonInputStyles = {
    bg: inputBg,
    color: inputColor,
    borderColor: inputBorderColor,
    _hover: {
      borderColor: inputHoverBorderColor
    },
    _focus: {
      borderColor: inputFocusBorderColor,
      boxShadow: `0 0 0 1px ${inputFocusBorderColor}`
    },
    borderRadius: "md",
    fontWeight: "medium",
    transition: "all 0.2s",
    fontSize: "12px",
    boxSizing: "border-box"
  };

  const argonSelectStyles = {
    ...argonInputStyles,
    iconSize: "20px",
    _active: {
      bg: inputBg
    }
  };

  const renderEditableCell = (row, rowIndex, column) => {
    const cellValue = row[column.key] !== undefined ? row[column.key] : "";
    const cellHasError = !!(cellErrors?.[rowIndex]?.[column.key]);

    const commonProps = {
      size: "sm",
      isDisabled,
      isReadOnly,
      value: cellValue,
      isInvalid: cellHasError,
      "aria-label": column.label,
      name: column.key || `${fieldId}-${rowIndex}-${column.key}`
    };

    if (column.component) {
      const CustomComponent = column.component;
      const adaptedOnChange = createChangeHandler(rowIndex, column.key, column);

      return (
        <CustomComponent
          {...commonProps}
          onChange={adaptedOnChange}
          rowIndex={rowIndex}
          columnKey={column.key}
          row={row}
          {...argonInputStyles}
          {...(column.componentProps || {})}
          {...(column.props || {})}
        />
      );
    }

    if (column.type && typeof column.type === 'string' && !['SearchSelect', 'select', 'number'].includes(column.type)) {
      const CustomComponentByType = componentTypes[column.type];
      if (CustomComponentByType) {
        const adaptedOnChange = createChangeHandler(rowIndex, column.key, column);

        return (
          <CustomComponentByType
            {...commonProps}
            onChange={adaptedOnChange}
            rowIndex={rowIndex}
            columnKey={column.key}
            row={row}
            {...argonInputStyles}
            {...(column.props || {})}
          />
        );
      }
    }

    switch (column.type) {
      case "select":
        return (
          <Select
            {...commonProps}
            onChange={(e) => handleCellChange(rowIndex, column.key, e.target.value)}
            {...argonSelectStyles}
            {...(column.props || {})}
          >
            {column.options?.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        );

      case "number":
        return (
          <Input
            {...commonProps}
            type="number"
            onChange={(e) => handleCellChange(rowIndex, column.key, e.target.value)}
            {...argonInputStyles}
            {...(column.props || {})}
          />
        );

      case "SearchSelect":
        return (
            <SearchSelect
                {...(column.props || {})}
                {...commonProps}
                onChange={e => {
                  handleCellChange(rowIndex, column.key, e.target.value)
                }}
                rowIndex={rowIndex}
                columnKey={column.key}
                row={row}
                {...argonInputStyles}
            />
        );

      case "time":
        return (
            <InputMask
                mask="99:99"
                value={cellValue}
                onChange={(e) => handleCellChange(rowIndex, column.key, e.target.value)}
            >
              {(inputProps) => (
                  <Input
                      {...commonProps}
                      {...inputProps}
                      placeholder="HH:MM"
                      {...argonInputStyles}
                      {...(column.props || {})}
                  />
              )}
            </InputMask>
        );

      default:
        return (
          <Input
            {...commonProps}
            onChange={(e) => handleCellChange(rowIndex, column.key, e.target.value)}
            {...argonInputStyles}
            {...(column.props || {})}
          />
        );
    }
  };

  return (
    <Box

      {...props}
    >
      <Flex justify="flex-end" mb={3}>
        <Button
          leftIcon={<AddIcon/>}
          bg={buttonBg}
          color={buttonColor}
          _hover={{bg: buttonHoverBg}}
          _active={{bg: buttonHoverBg}}
          size="sm"
          fontSize="sm"
          onClick={handleAddRow}
          isDisabled={isDisabled || isReadOnly}
          fontWeight="bold"
          borderRadius="md"
          boxShadow="md"
          variant="primary"
        >
          {addRowLabel}
        </Button>
      </Flex>

      <Box overflowX="auto">
        <Table variant="simple" size="sm">
          <Thead bg={headerBg}>
            <Tr>
              {columns.map((column) => (
                <Th
                  key={column.key}
                  fontSize="xs"
                  fontWeight="bold"
                  textTransform="uppercase"
                  py={3}
                  color={useColorModeValue("gray.600", "gray.300")}
                >
                  {column.label}
                </Th>
              ))}
              <Th width="60px"></Th>
            </Tr>
          </Thead>
          <Tbody>
            {rows.length > 0 ? (
              rows.map((row, rowIndex) => (
                <Tr key={rowIndex} _hover={{bg: useColorModeValue("gray.50", "gray.700")}}>
                  {columns.map((column) => (
                    <Td key={`${rowIndex}-${column.key}`} py={2} borderColor={borderColor}>
                      {renderEditableCell(row, rowIndex, column)}
                    </Td>
                  ))}
                  <Td borderColor={borderColor}>
                    <IconButton
                      icon={<DeleteIcon/>}
                      color={deleteIconColor}
                      variant="ghost"
                      size="sm"
                      aria-label="Remover linha"
                      onClick={() => handleRemoveRow(rowIndex)}
                      isDisabled={isDisabled || isReadOnly}
                      _hover={{bg: useColorModeValue("red.50", "rgba(254, 178, 178, 0.12)")}}
                    />
                  </Td>
                </Tr>
              ))
            ) : (
              <Tr>
                <Td colSpan={columns.length + 1} textAlign="center" py={4} fontSize="sm"
                    color={useColorModeValue("gray.500", "gray.400")}>
                  { errorMessage ? (<FormErrorMessage>{errorMessage}</FormErrorMessage>) : (emptyMessage) }
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Box>
      <ConfirmDialog ref={deleteDialogRef} onConfirm={handleConfirmRemove} confirmButtonProps={{ colorScheme: "red" }} />
    </Box>
  );
});

DynamicTable.displayName = "DynamicTable";

export default DynamicTable;
